import {
  Box,
  Icon, IconName,
  Text,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import { useContext, useState } from 'react';
import { UserContext } from '~/dashboard/context';
import { useGetDataSourcesStatuses } from '~/entities/hooks';
import checkPermissions from '~/evaluations/helpers/permissions';
import type { DataSources } from '../types';
import { ServiceResponseModalNew } from './ServiceResponseModalNew';
import {
  NoDataText, ServicesItem, ServicesItemText, ServicesList,
} from './styles';

interface Props {
  evaluationToken: string;
  finalResponse?: {
    formatted_responses?: {
      [key: string]: {};
    };
    raw_responses?: Record<string, unknown>;
  };
}
type ServiceData = DataSources['statuses_of_all_service_nodes_ran_in_workflow'][string];

export const ServicesListNew = ({ evaluationToken, finalResponse }: Props) => {
  const { data, isLoading } = useGetDataSourcesStatuses(evaluationToken);
  const servicesList = data?.statuses_of_all_service_nodes_ran_in_workflow !== undefined
    ? Object.values(data?.statuses_of_all_service_nodes_ran_in_workflow)
    : [];

  if (isLoading) {
    return (
      <ServicesList>
        <NoDataText size={3}>loading...</NoDataText>
      </ServicesList>
    );
  }

  if (servicesList.length === 0) {
    return (
      <ServicesList>
        <NoDataText size={3}>No data sources run for this evaluation.</NoDataText>
      </ServicesList>
    );
  }

  return (
    <ServicesList>
      {servicesList.map(service => (
        <Service
          key={`${service.name}${service.action_id}`}
          service={service}
          evaluationToken={evaluationToken}
          formattedResponse={finalResponse?.formatted_responses?.[service.name]}
          rawResponse={finalResponse?.raw_responses?.[service.name]?.[0]}
        />
      ))}
    </ServicesList>
  );
};

function Service(
  {
    service, evaluationToken, formattedResponse, rawResponse,
  }:
  {
    service: ServiceData;
    evaluationToken: string;
    formattedResponse?: {};
    rawResponse?: {};
  },
) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { featureFlags, permissions } = useContext(UserContext);
  const hasPermissions = checkPermissions(permissions, ['dob', 'ssn']);
  const hasPiiPermissionsFeatureFlag = featureFlags?.forbidUnpermittedAgentsSeeingPiiResponses;
  const cannotSeeServiceMetadata = hasPiiPermissionsFeatureFlag && !hasPermissions;
  const snackbar = useSnackBar();
  const name = service.modifiedName ?? service.name;

  const handleOnClick = () => {
    if (service.status === 'not_executed') {
      return;
    }
    if (cannotSeeServiceMetadata) {
      snackbar({ title: 'You do not have permission to see this service metadata.', type: 'danger' });
      return;
    }
    setIsModalOpen(true);
  };

  const tooltipContent = (
    <Text style={{ textTransform: 'capitalize', color: 'var(--cl-text-accent)' }}>
      {service.status.replace('_', ' ')}
    </Text>
  );

  return (
    <>
      <Tooltip content={tooltipContent} delayDuration={0} side="left">
        <ServicesItem onClick={handleOnClick} showCursor={service.status !== 'not_executed'} data-dd-action-name="alloydd-new-data-sources" data-testid="service-item-button">
          <Box direction="row" gap={2} justifyContent="flex-start">
            {(service.status === 'executed' || service.status === 'cached')
            && (<Icon icon={IconName.CircleCheckFilled} size="large" color="var(--cl-green-300)" />)}
            {(service.status === 'failed')
            && (<Icon icon={IconName.CircleCloseFilled} size="large" color="var(--cl-red-300)" />)}
            {(service.status === 'not_executed')
            && (<Icon icon={IconName.CircleMinusFilled} size="large" color="black" />)}
            <ServicesItemText weight={700}>
              {name}
            </ServicesItemText>
          </Box>
        </ServicesItem>
      </Tooltip>
      {isModalOpen && (
      <ServiceResponseModalNew
        closeModal={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        title={name}
        evaluationToken={evaluationToken}
        actionId={service.action_id}
        formattedResponse={formattedResponse}
        rawResponse={rawResponse}
      />
      )}
    </>
  );
}

export default ServicesListNew;
